import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "../i18n/config";
import "./index.css";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "../routes/index";
import { initGravity } from "../external/gravity";
import { Logger } from "../utils/logger";
import { setupDataAttributeTracking } from "../lib/analytics/setupDataAttributeTracking";
import { SessionMetrics } from "../domains/analytics/services/SessionMetrics";
import { registerGenogramFieldPacks } from "../domains/genogram/fields";
import "../lib/createlyLegacyConverterBrowser";
import { PostHogProvider } from 'posthog-js/react'
import { useTeamPortalLanguageBridge } from "../hooks/useTeamPortalLanguageBridge";
import { GrowthBookProvider } from "../lib/growthbook/GrowthBookProvider";
import { registerCanvasPlugins } from '../core/canvas/registerCanvasPlugins';
import { registerDefaultProfiles, registerDefaultConnectors } from '../core/data-profiles';
import { registerNetworkDomain } from '../domains/network/shapes/register';
import { registerCompassDomain } from '../domains/compass/presets/register';
import { registerPlanningDomain } from '../domains/planning/shapes/register';
import { registerSoftwareDesignDomain } from '../domains/software-design/shapes/register';
import { registerCanvasProjectionPanes } from '../domains/canvas-projections/registerCanvasProjectionPanes';
import { registerDomainProfile } from '../core/data-profiles';
import { ProcessExtractionProfile } from '../domains/process-capture/profiles/ProcessExtractionProfile';
import { registerFolderCoverageProvider } from '../core/folderCoverage/registry';
import { processFolderCoverageProvider } from '../domains/process/services/folderCoverage/ProcessFolderCoverageService';

const CHUNK_RELOAD_GUARD_KEY = "chunk-load-recovery-attempted";

const isChunkLoadFailure = (reason: unknown): boolean => {
  const errorLike = reason as { name?: string; message?: string } | undefined;
  const name = (errorLike?.name || "").toLowerCase();
  const message = (errorLike?.message || String(reason || "")).toLowerCase();

  return (
    name.includes("chunkloaderror") ||
    message.includes("loading chunk") ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("importing a module script failed")
  );
};

const installChunkLoadRecovery = (): void => {
  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadFailure(event.reason)) {
      return;
    }

    // Prevent infinite reload loops if a deeper outage persists.
    if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === "1") {
      return;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, "1");
    window.location.reload();
  });
};

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
  session_recording: {
    // Konva already owns the visual canvas. Pixel-frame capture continuously
    // reads and fingerprints that surface, which is prohibitively expensive
    // for large interactive diagrams. Keep DOM/session replay without canvas
    // frames; product analytics events remain unaffected.
    captureCanvas: {
      recordCanvas: false,
    },
  },
} as const

// Initialize logger with environment settings
Logger.initialize({ LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL });

// Boot-time side effects (domain registration, tracking wiring) run before
// `createRoot().render()`, which is where <PostHogProvider> — and therefore
// PostHog `a.*` capture and `$exception` autocapture — is initialized. An
// unguarded throw in any of these steps would prevent React from mounting,
// silently taking analytics AND error tracking dark for that session while
// GA4/Brevo (lazy-initialized inside Tracker.track) kept limping. Isolate
// each step so one failing registration can never block the app mount or the
// observability pipeline. See issue #1747.
const bootStep = (label: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    Logger.error(`Boot step "${label}" failed`, error);
  }
};

// Register dynamic canvas plugins (Issue #998)
bootStep("registerCanvasPlugins", registerCanvasPlugins);
bootStep("registerCanvasProjectionPanes", registerCanvasProjectionPanes);

// Eagerly register the network domain's shapes / connectors / view
// profiles / presets. NetworkCanvasPlugin also registers these
// lazily when a network diagram opens, but the Compass Explorer
// renders `compass_bubble` / `compass_env_label` nodes as soon as
// the folder graph arrives — in practice that beats the plugin's
// async register() and `NodesRenderer` warns "no manifest." Calling
// it here at boot closes the race without changing the plugin
// contract. `registerShape` is idempotent, so the plugin's own
// registration later is a no-op.
bootStep("registerNetworkDomain", registerNetworkDomain);

// Register the process domain's data profile so the
// OntologyPropertyMapper can fold extracted props through
// `propsToAttrsMapping` for any folder with `domain='process'`.
// See plans/compass/audit-deliverable-plan.md §WI#1.
bootStep("registerDomainProfile", () => registerDomainProfile(ProcessExtractionProfile));

// Register Compass Explorer viewpoint presets into the shared
// viewRulesStore. Compass reuses the network diagram type, so
// domain-gated presets surface through the same UnifiedRulesPanel
// path that Network uses.
bootStep("registerCompassDomain", registerCompassDomain);

// Register the strategy & planning domain (Epic #1385). Idempotent, same
// pattern as registerNetworkDomain. Epic SP-A / SP-B replace the scaffold
// reference shapes with real renderers; calling here at boot ensures the
// 17 enriched table semantic schemas are available before any table
// document opens.
bootStep("registerPlanningDomain", registerPlanningDomain);

// Register the software-design vertical (Epic A / #1375). Idempotent —
// landing the bootstrap call here completes Step 0.1 of the playbook so
// the 11 software-design domains' shape categories surface in the picker.
// See plans/software-design/playbook.md § X (App affordance wiring).
bootStep("registerSoftwareDesignDomain", registerSoftwareDesignDomain);

// Folder coverage providers — the dashboard's Sources section reads
// fidelity layers from the registered provider for the folder's
// domain. Network's provider is registered inside
// `registerNetworkDomain`; process folders don't have a central
// domain bootstrap yet, so we register the process provider here.
bootStep("registerFolderCoverageProvider", () => registerFolderCoverageProvider(processFolderCoverageProvider));
bootStep("installChunkLoadRecovery", installChunkLoadRecovery);

// Register genogram field packs
bootStep("registerGenogramFieldPacks", registerGenogramFieldPacks);

// Register domain profiles + data-source connector manifests so the
// diagram editor can open ImportSourceModal against the active folder.
bootStep("registerDefaultProfiles", registerDefaultProfiles);
bootStep("registerDefaultConnectors", registerDefaultConnectors);

const gravity = initGravity({
  siteUrl: import.meta.env.VITE_SITE_URL
});
// gravity.init();
window.gravity = gravity;

bootStep("setupDataAttributeTracking", setupDataAttributeTracking);
bootStep("SessionMetrics.init", () => SessionMetrics.init());
// Start timer for time-to-first-shape efficiency metric
bootStep("SessionMetrics.startTimer", () => SessionMetrics.startTimer('time_to_first_shape'));

function LanguageBridge() {
  useTeamPortalLanguageBridge(true)
  return null
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
      <GrowthBookProvider>
        <LanguageBridge />
        <RouterProvider router={router} />
      </GrowthBookProvider>
    </PostHogProvider>
  </StrictMode>,
);
